import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  GitCompare,
  History,
  Loader2,
  Search,
} from "lucide-react";
import {
  getDigitalPassport,
  getDigitalPassportReport,
} from "../../services/apiService";
import { getMediaBaseUrl } from "../../config/clientConfig";
import { toHistory, toComparison, verdictLabels } from "../digitalPassport";

/**
 * Digital Passport — has this vehicle been inspected before, and was the damage
 * on this claim already there?
 *
 * Two legacy-portal endpoints (session-cookie auth, minted automatically by
 * apiService.establishPortalSession):
 *   POST /digital_passport/query   the vehicle's other inspections
 *   POST /digital_passport/report  one of them paired with the current case
 *
 * The comparison is capped at MAX_COMPARE, matching the portal.
 *
 * The comparison is shown as a per-part verdict rather than two lists side by
 * side, because the reviewer's question is per part: was the grille already
 * broken? New damage is sorted first and counted in the header — that is the
 * finding that changes a decision.
 */

const MAX_COMPARE = 3;

// search_param selects what the lookup is keyed on: the portal sends 1 for a
// chassis-number search and 2 for a plate search.
const SEARCH_MODES = [
  { param: 2, id: "plate", label: "Number plate", placeholder: "UP86AE3602" },
  { param: 1, id: "vin", label: "Chassis number", placeholder: "MALPB813LLM087813" },
];

const TONES = {
  bad: { fg: "#c22f2f", bg: "rgba(194,47,47,0.10)", border: "rgba(194,47,47,0.30)" },
  warn: { fg: "#9a6212", bg: "rgba(154,98,18,0.10)", border: "rgba(154,98,18,0.30)" },
  good: { fg: "#1c7a43", bg: "rgba(28,122,67,0.10)", border: "rgba(28,122,67,0.28)" },
  muted: {
    fg: "var(--cl-text-faint)",
    bg: "transparent",
    border: "var(--cl-border-control)",
  },
};

const card = "rounded-card border border-cl-card bg-cl-surface overflow-hidden";
const cardHead =
  "flex flex-wrap items-center justify-between gap-3 border-b border-cl-head px-4 py-3";
const cardTitle = "text-[13.5px] font-semibold text-cl-text";
const labelClass =
  "text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label";

const mediaUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${getMediaBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const Pill = ({ tone = "muted", children, title }) => {
  const t = TONES[tone] || TONES.muted;
  return (
    <span
      title={title}
      className="inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-[2px] text-[10.5px] font-semibold uppercase tracking-[0.04em]"
      style={{ color: t.fg, background: t.bg, borderColor: t.border }}
    >
      {children}
    </span>
  );
};

function SessionNotice() {
  return (
    <div className="px-4 py-10 text-center">
      <div className="flex items-center justify-center gap-2 text-[13.5px] font-semibold text-cl-text">
        <AlertTriangle className="h-4 w-4 text-[#9a6212]" />
        Portal session unavailable
      </div>
      <p className="mx-auto mt-2 max-w-[560px] text-[12px] leading-[1.6] text-cl-muted">
        The passport is served by the legacy portal, which uses its own session
        cookie. The app mints one automatically from your login, so this usually
        means the portal rejected the token — try signing in again. It also cannot
        work at all if this app is served from a domain other than the portal's,
        since the cookie is not sent cross-site.
      </p>
    </div>
  );
}

/**
 * One half of a side-by-side pair.
 *
 * Distinguishes "this inspection did not capture this view" from "the file is
 * gone": hiding a failed image leaves an unexplained empty box, which reads as a
 * layout bug rather than as missing evidence.
 */
function PairCell({ path, alt, aspect = "aspect-[4/3]" }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className="overflow-hidden rounded-tile border border-cl-card bg-cl-canvas">
      {path && !failed ? (
        <img
          src={mediaUrl(path)}
          alt={alt}
          loading="lazy"
          // Contained, not cropped. These frames are the evidence being
          // compared: a panel crop is a wide strip, and object-cover in a 4:3
          // box throws away its ends — including, on an annotated frame, some
          // of the damage labels. Letterboxing is the lesser cost.
          className={`block ${aspect} w-full object-contain`}
          onError={() => setFailed(true)}
        />
      ) : (
        <div
          className={`flex ${aspect} w-full items-center justify-center px-2 text-center text-[11px] text-cl-faint`}
        >
          {path ? "Image unavailable" : "Not captured"}
        </div>
      )}
    </div>
  );
}

/**
 * The frames to show for one panel.
 *
 * A panel is photographed from several angles; the useful one is whichever
 * angle both inspections captured it from, so the two frames are of the same
 * thing. Failing that, take the angle that has anything at all — a single frame
 * still tells you what the panel looks like now.
 */
const framesForPart = (comparison, partName) => {
  const candidates = (comparison.sideViews || [])
    .map((view) => ({ view, part: view.parts.find((p) => p.name === partName) }))
    .filter((entry) => entry.part);

  return (
    candidates.find((e) => e.part.prior && e.part.current) ||
    candidates.find((e) => e.part.current) ||
    candidates[0] ||
    null
  );
};

/** Two frames of the same thing, wiped between rather than set apart. */
function Blend({ prior, current, blend, onBlend, comparedLabel, thisLabel }) {
  return (
    <>
      <div className="relative overflow-hidden rounded-tile border border-cl-card bg-cl-canvas">
        <img
          src={mediaUrl(prior)}
          alt={`${comparedLabel}`}
          className="block aspect-[2/1] w-full object-contain"
        />
        <img
          src={mediaUrl(current)}
          alt={`${thisLabel}`}
          style={{ opacity: blend / 100 }}
          className="absolute inset-0 h-full w-full object-contain"
        />
      </div>
      <div className="mt-2 flex items-center gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-cl-label">
          {comparedLabel}
        </span>
        <input
          type="range"
          min="0"
          max="100"
          value={blend}
          onChange={(e) => onBlend(Number(e.target.value))}
          aria-label="Blend between the two inspections"
          className="h-[3px] flex-1 accent-[color:var(--cl-accent)]"
        />
        <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-cl-accent">
          {thisLabel}
        </span>
      </div>
    </>
  );
}

/**
 * The comparison, as one screen rather than a page.
 *
 * Panels on the left in their own scroller, the selected panel's evidence
 * pinned on the right. The height is fixed to the visible content area, so a
 * vehicle with forty damaged panels reads exactly like one with two — before
 * this, every extra panel pushed the photographs further down the page, and the
 * reviewer scrolled past the answer to find the evidence for it.
 *
 * 165px is the chrome above and below this tab's scroll container, measured in
 * the browser (139px of header and tabs, 26px of padding).
 */
function Comparison({ comparison, onBack }) {
  const [partName, setPartName] = useState(null);
  const [blend, setBlend] = useState(null);

  const earlier = comparison.comparedIsEarlier;
  const labels = verdictLabels(earlier === true);
  const comparedLabel = formatDate(comparison.prior.assessedAt);
  const thisLabel = "this case";

  // comparison.parts already leads with what needs a decision.
  const ordered = comparison.parts;
  const active = ordered.find((p) => p.name === partName) || ordered[0] || null;
  const frames = active ? framesForPart(comparison, active.name) : null;
  const canBlend = Boolean(frames?.part?.prior && frames?.part?.current);
  const changed = ordered.filter((p) => p.verdict !== "clear").length;

  const verdict = active ? labels[active.verdict] || labels.clear : null;
  const tone = verdict ? TONES[verdict.tone] || TONES.muted : TONES.muted;

  return (
    <div className="flex h-[calc(100vh-165px)] min-h-[460px] flex-col overflow-hidden rounded-card border border-cl-card bg-cl-surface">
      <div className={cardHead}>
        <div className="min-w-0">
          <span className={cardTitle}>
            Comparing against {comparison.prior.inspectionLabel} ·{" "}
            {comparison.prior.refNum}
          </span>
          <div className="mt-[2px] text-[11.5px] text-cl-muted">
            {comparedLabel}
            {earlier === true && " · before this case"}
            {earlier === false && " · after this case"}
          </div>
        </div>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex-none rounded-tile border border-cl-control px-[11px] py-[5px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
          >
            Back to history
          </button>
        )}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 sm:grid-cols-[212px_1fr]">
        {/* Panels. Its own scroller, so the count never moves the evidence. */}
        <div className="cl-scroll min-h-0 overflow-y-auto border-b border-cl-head bg-cl-canvas sm:border-b-0 sm:border-r">
          <div className="sticky top-0 border-b border-cl-head bg-cl-canvas px-[13px] py-[9px] text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
            {changed} differ · {ordered.length - changed} clear in both
          </div>
          {ordered.map((part) => {
            const isActive = active && part.name === active.name;
            const isClear = part.verdict === "clear";
            return (
              <button
                key={part.name}
                type="button"
                onClick={() => {
                  setPartName(part.name);
                  setBlend(null);
                }}
                className={`flex w-full items-center gap-[9px] border-b border-cl-row border-l-2 px-[13px] py-[9px] text-left transition-colors ${
                  isActive
                    ? "border-l-cl-accent bg-cl-surface"
                    : "border-l-transparent hover:bg-cl-hover"
                }`}
              >
                <span
                  className="h-[8px] w-[8px] flex-none rounded-[3px]"
                  style={{
                    background: isClear
                      ? "var(--cl-border-control)"
                      : (TONES[(labels[part.verdict] || labels.clear).tone] || TONES.muted).fg,
                  }}
                />
                <span
                  className={`min-w-0 flex-1 truncate text-[12.5px] ${
                    isClear
                      ? "font-medium text-cl-muted"
                      : "font-semibold text-cl-text"
                  }`}
                >
                  {part.name}
                </span>
                <span
                  className={`flex-none text-[10.5px] font-bold ${
                    isClear ? "text-cl-faint" : "text-cl-accent"
                  }`}
                >
                  {isClear ? "clear" : (part.currentIntensity ?? part.priorIntensity ?? "—")}
                </span>
              </button>
            );
          })}
        </div>

        {/* Evidence for the selected panel, pinned. */}
        <div className="cl-scroll min-h-0 overflow-y-auto px-4 py-[13px]">
          {!active ? (
            <p className="py-10 text-center text-[12.5px] text-cl-muted">
              Neither inspection assessed any panel in common.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[14.5px] font-bold text-cl-text">
                  {active.name}
                </span>
                <span
                  className="rounded-pill px-[9px] py-[2px] text-[10px] font-bold uppercase tracking-[0.03em]"
                  style={{ background: tone.bg, color: tone.fg }}
                >
                  {verdict.label}
                </span>
                {canBlend && (
                  <span className="ml-auto flex gap-[2px] rounded-tile bg-cl-bg p-[2px]">
                    {[
                      { id: "split", label: "Side by side" },
                      { id: "blend", label: "Fade" },
                    ].map((option) => {
                      const on =
                        (option.id === "blend") === (blend !== null);
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() =>
                            setBlend(option.id === "blend" ? 100 : null)
                          }
                          aria-pressed={on}
                          className={`rounded-[5px] px-[9px] py-[3px] text-[11px] font-semibold transition-colors ${
                            on
                              ? "bg-cl-text text-white"
                              : "text-cl-muted hover:text-cl-secondary"
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </span>
                )}
              </div>

              <div className="mt-[3px] text-[11.5px] text-cl-muted">
                {active.currentTypes.length
                  ? `${active.currentTypes.join(", ")} · `
                  : ""}
                {active.currentIntensity ?? "—"}/10 on {thisLabel},{" "}
                {active.priorIntensity == null
                  ? "not assessed"
                  : active.priorIntensity
                    ? `${active.priorIntensity}/10`
                    : "clear"}{" "}
                on {comparedLabel}
                {frames?.view ? ` · seen from ${frames.view.label}` : ""}
              </div>

              <div className="mt-3">
                {!frames ? (
                  <p className="rounded-tile bg-cl-canvas px-3 py-8 text-center text-[12px] text-cl-muted">
                    Neither inspection photographed this panel.
                  </p>
                ) : blend !== null && canBlend ? (
                  <Blend
                    prior={frames.part.prior}
                    current={frames.part.current}
                    blend={blend}
                    onBlend={setBlend}
                    comparedLabel={comparedLabel}
                    thisLabel={thisLabel}
                  />
                ) : (
                  <>
                    <div className="mb-[5px] grid grid-cols-2 gap-3 text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                      <span>{comparedLabel}</span>
                      <span className="text-cl-accent">{thisLabel}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <PairCell
                        path={frames.part.prior}
                        alt={`${active.name} — ${comparedLabel}`}
                        aspect="aspect-[2/1]"
                      />
                      <PairCell
                        path={frames.part.current}
                        alt={`${active.name} — ${thisLabel}`}
                        aspect="aspect-[2/1]"
                      />
                    </div>
                  </>
                )}
              </div>

              {frames?.view && (frames.view.prior || frames.view.current) && (
                <div className="mt-4">
                  <div className={labelClass}>
                    {frames.view.label} view · for context
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-3">
                    <PairCell
                      path={frames.view.prior}
                      alt={`${frames.view.label} — ${comparedLabel}`}
                    />
                    <PairCell
                      path={frames.view.current}
                      alt={`${frames.view.label} — ${thisLabel}`}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* A comparison that runs the wrong way in time cannot answer the question
          it is being asked. Saying so beats leaving a reviewer to notice the
          dates. */}
      {earlier === false && (
        <p className="flex-none border-t border-cl-head bg-[#fdf6e7] px-4 py-[10px] text-[12px] text-[#7a5a12]">
          <b className="font-semibold text-[#5c430c]">
            This inspection is later than the claim.
          </b>{" "}
          It cannot establish pre-existing damage — only what the vehicle looked
          like afterwards.
        </p>
      )}
    </div>
  );
}
export default function DigitalPassport({
  refNum,
  vin,
  numPlate,
  initialHistory = null,
}) {
  const [mode, setMode] = useState(() =>
    !numPlate && vin ? SEARCH_MODES[1] : SEARCH_MODES[0],
  );
  const [term, setTerm] = useState(() => numPlate || vin || "");

  // Seeded from the lookup the case page already ran for its banner, so opening
  // the tab shows the history at once instead of querying for it a second time.
  const [state, setState] = useState({
    loading: false,
    error: null,
    data: initialHistory,
  });
  const [selected, setSelected] = useState([]);
  const [report, setReport] = useState({ loading: false, error: null, data: null });
  const [shown, setShown] = useState(0);

  const search = useCallback(
    (searchMode, value) => {
      if (!refNum || !value) return;
      setSelected([]);
      setReport({ loading: false, error: null, data: null });
      setState({ loading: true, error: null, data: null });
      getDigitalPassport({
        refNum,
        searchParam: searchMode.param,
        vin: searchMode.id === "vin" ? value : "",
        numPlate: searchMode.id === "plate" ? value : "",
      })
        .then((raw) =>
          setState({
            loading: false,
            error: null,
            data: toHistory(raw, { currentRefNum: refNum }),
          }),
        )
        .catch((err) => setState({ loading: false, error: err, data: null }));
    },
    [refNum],
  );

  useEffect(() => {
    if (initialHistory) return;
    const initial = numPlate || vin;
    if (initial) {
      search(!numPlate && vin ? SEARCH_MODES[1] : SEARCH_MODES[0], initial);
    }
  }, [numPlate, vin, search, initialHistory]);

  const toggle = useCallback((key) => {
    setSelected((current) =>
      current.includes(key)
        ? current.filter((k) => k !== key)
        : current.length >= MAX_COMPARE
          ? current
          : [...current, key],
    );
  }, []);

  const history = state.data;
  const cases = history?.cases || [];

  const runComparison = useCallback(async () => {
    const chosen = cases.filter((c) => selected.includes(c.refNum));
    if (!chosen.length) return;
    setReport({ loading: true, error: null, data: null });
    try {
      const results = await Promise.all(
        chosen.map((entry) =>
          getDigitalPassportReport({
            refNum: entry.refNum,
            sourceName: entry.sourceName,
            currentRefNum: refNum,
          }).then(toComparison),
        ),
      );
      setReport({ loading: false, error: null, data: results.filter(Boolean) });
    } catch (err) {
      setReport({ loading: false, error: err, data: null });
    }
  }, [cases, selected, refNum]);

  const historyView = (
    <div className="flex flex-col gap-3">
      <div className={card}>
        <div className={cardHead}>
          <div className="flex items-center gap-2">
            <History className="h-[15px] w-[15px] text-cl-accent" />
            <span className={cardTitle}>Vehicle history</span>
          </div>
          {history && (
            <span className="text-[11px] text-cl-faint">
              {history.priorCount
                ? `${history.priorCount} earlier inspection${
                    history.priorCount === 1 ? "" : "s"
                  }`
                : "no earlier inspection"}
            </span>
          )}
        </div>

        <form
          className="flex flex-wrap items-end gap-3 border-b border-cl-head px-4 py-[13px]"
          onSubmit={(e) => {
            e.preventDefault();
            search(mode, term.trim());
          }}
        >
          <div>
            <div className={labelClass}>Search by</div>
            <div className="mt-[5px] flex gap-1 rounded-tile bg-cl-tint p-[3px]">
              {SEARCH_MODES.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setMode(option)}
                  aria-pressed={mode.id === option.id}
                  className={`rounded-[6px] px-[10px] py-[5px] text-[12px] font-semibold transition-colors ${
                    mode.id === option.id
                      ? "bg-cl-surface text-cl-accent shadow-[0_1px_2px_rgba(19,23,32,0.08)]"
                      : "text-cl-faint hover:text-cl-secondary"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="min-w-[200px] flex-1">
            <label className={labelClass} htmlFor="passport-term">
              {mode.label}
            </label>
            <input
              id="passport-term"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={mode.placeholder}
              className="mt-[5px] w-full rounded-tile border border-cl-control bg-cl-surface px-[10px] py-[6px] text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
            />
          </div>

          <button
            type="submit"
            disabled={!term.trim() || state.loading}
            className={`flex items-center gap-[6px] rounded-tile px-3 py-[7px] text-[12.5px] font-semibold transition-colors ${
              term.trim() && !state.loading
                ? "bg-cl-accent text-white hover:bg-cl-accent-hover"
                : "cursor-not-allowed bg-cl-tint text-cl-disabled"
            }`}
          >
            <Search className="h-[13px] w-[13px]" />
            Search
          </button>
        </form>

        {state.loading ? (
          <div className="flex items-center justify-center gap-2 py-14 text-[13px] text-cl-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Looking up vehicle history…
          </div>
        ) : state.error?.notLoggedIn ? (
          <SessionNotice />
        ) : state.error ? (
          <div className="px-4 py-10 text-center">
            <div className="text-[13.5px] font-semibold text-cl-text">
              Could not load the digital passport
            </div>
            <p className="mt-1 text-[12px] text-cl-muted">{state.error.message}</p>
          </div>
        ) : !history ? (
          <p className="px-4 py-10 text-center text-[12.5px] text-cl-muted">
            Enter a {mode.label.toLowerCase()} to look up this vehicle's history.
          </p>
        ) : cases.length === 0 ? (
          <p className="px-4 py-10 text-center text-[12.5px] text-cl-muted">
            No inspection was found for this vehicle. This appears to be its first
            time through.
          </p>
        ) : (
          <div className="divide-y divide-cl-row">
            {cases.map((entry) => {
              const isSelected = selected.includes(entry.refNum);
              const atCap = !isSelected && selected.length >= MAX_COMPARE;
              // Comparing the case with itself tells you nothing.
              const selectable = !entry.isCurrent && !atCap;

              return (
                <div
                  key={entry.refNum}
                  className={`flex items-center gap-3 px-4 py-[11px] ${
                    entry.isCurrent ? "bg-cl-tint/40" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={!selectable}
                    onChange={() => toggle(entry.refNum)}
                    aria-label={`Compare ${entry.refNum}`}
                    title={
                      entry.isCurrent
                        ? "This is the case you are viewing"
                        : atCap
                          ? `Up to ${MAX_COMPARE} at a time`
                          : `Compare ${entry.refNum}`
                    }
                    className="h-[14px] w-[14px] shrink-0 accent-[color:var(--cl-accent)] disabled:opacity-40"
                  />

                  {entry.coverPic ? (
                    <img
                      src={mediaUrl(entry.coverPic)}
                      alt=""
                      className="h-11 w-16 shrink-0 rounded-tile border border-cl-card object-cover"
                      onError={(e) => {
                        e.currentTarget.style.visibility = "hidden";
                      }}
                    />
                  ) : (
                    <div className="h-11 w-16 shrink-0 rounded-tile border border-dashed border-cl-control" />
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-[12.5px] font-semibold text-cl-text">
                        {entry.refNum}
                      </span>
                      <Pill tone={entry.inspectionType === "claim" ? "warn" : "muted"}>
                        {entry.inspectionLabel}
                      </Pill>
                      {entry.isCurrent && <Pill tone="good">This case</Pill>}
                    </div>
                    <div className="mt-[2px] text-[11.5px] text-cl-faint">
                      Assessed {formatDate(entry.assessedAt)}
                      {entry.numPlate ? ` · ${entry.numPlate}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {cases.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-cl-head px-4 py-[11px]">
            <span className="text-[11.5px] text-cl-muted">
              {selected.length} of {MAX_COMPARE} selected
              {selected.length >= MAX_COMPARE && " — limit reached"}
            </span>
            <button
              type="button"
              onClick={runComparison}
              disabled={!selected.length || report.loading}
              className={`flex items-center gap-2 rounded-tile px-3 py-[7px] text-[12.5px] font-semibold transition-colors ${
                selected.length && !report.loading
                  ? "bg-cl-accent text-white hover:bg-cl-accent-hover"
                  : "cursor-not-allowed bg-cl-tint text-cl-disabled"
              }`}
            >
              {report.loading ? (
                <Loader2 className="h-[13px] w-[13px] animate-spin" />
              ) : (
                <GitCompare className="h-[13px] w-[13px]" />
              )}
              Compare with this case
            </button>
          </div>
        )}
      </div>

      {report.error?.notLoggedIn && <div className={card}><SessionNotice /></div>}

      {report.error && !report.error.notLoggedIn && (
        <div className={`${card} px-4 py-6 text-center`}>
          <p className="text-[12.5px] text-cl-muted">
            Comparison failed: {report.error.message}
          </p>
        </div>
      )}

    </div>
  );

  const comparisons = report.data || [];
  if (!comparisons.length) return historyView;

  // The comparison replaces the history rather than appending to it. Stacked
  // below, it started a screen down and every panel pushed it further; the
  // reviewer scrolled past the answer to reach the evidence for it.
  const active = comparisons[Math.min(shown, comparisons.length - 1)];

  return (
    <div className="flex flex-col gap-3">
      {comparisons.length > 1 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className={`${labelClass} mr-1`}>Compared with</span>
          {comparisons.map((entry, i) => (
            <button
              key={entry.prior.refNum}
              type="button"
              onClick={() => setShown(i)}
              aria-pressed={i === shown}
              className={`rounded-pill px-[11px] py-[5px] text-[11.5px] font-semibold transition-colors ${
                i === shown
                  ? "bg-cl-text text-white"
                  : "bg-cl-surface text-cl-secondary hover:bg-cl-hover"
              }`}
            >
              {formatDate(entry.prior.assessedAt)}
            </button>
          ))}
        </div>
      )}

      <Comparison
        key={active.prior.refNum}
        comparison={active}
        onBack={() => {
          setShown(0);
          setReport({ loading: false, error: null, data: null });
        }}
      />
    </div>
  );
}
